import React, { useState, useEffect } from 'react';
import { fetchData, postData } from '../utils/runtime.js';
import { trackEvent } from '../utils/bridge.js';

export default function SupportChatModal({ visible, setVisible }) {
    const [email, setEmail] = useState('');
    const [ticket, setTicket] = useState('');
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [file, setFile] = useState(null);
    const [started, setStarted] = useState(false);
    const [emailError, setEmailError] = useState('');

    const isValidEmail = (em) => {
        return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em);
    };

    useEffect(() => {
        if (visible) {
            trackEvent('support_modal_opened');
        }
        if (visible && started) {
            loadMessages();
        }
    }, [visible, started]);

    const loadMessages = () => {
        if (!email && !ticket) return;
        const query = ticket ? `ticket=${encodeURIComponent(ticket)}` : `email=${encodeURIComponent(email)}`;
        fetchData(`/api/support/get_messages?${query}`)
            .then(data => {
                if (data && data.messages) {
                    setMessages(data.messages);
                    if (data.ticket) setTicket(data.ticket);
                }
            })
            .catch(err => console.error('Failed to load messages', err));
    };

    const toBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

    const sendMessage = async () => {
        if (!input && !file) return;
        let imageData = null;
        if (file) {
            imageData = await toBase64(file);
            setFile(null);
        }
        trackEvent('support_message_send_pressed', { has_image: Boolean(imageData) })
        postData('/api/support/send_message', { email, ticket, message: input, image: imageData })
            .then((data) => {
                trackEvent('support_message_sent', { has_image: Boolean(imageData) })
                setInput('');
                if (data.ticket) setTicket(data.ticket);
                const now = Date.now() / 1000;
                const newMessages = [{ from: 'user', message: input, time: now }];
                if (data.auto_reply) {
                    newMessages.push(data.auto_reply);
                }
                setMessages(prev => [...prev, ...newMessages]);
                loadMessages();
            })
            .catch(err => { trackEvent('support_message_failed'); console.error('Failed to send message', err); });
    };

    const resolve = () => {
        trackEvent('support_resolved_clicked')
        postData('/api/support/resolve', { email, ticket })
            .then(() => {
                trackEvent('support_resolved_success')
                setVisible(false);
            })
            .catch(err => console.error('Failed to resolve', err));
    };

    useEffect(() => {
        if (!visible) {
            setStarted(false);
            setEmail('');
            setTicket('');
            setMessages([]);
            setInput('');
            setFile(null);
        }
    }, [visible]);

    if (!visible) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-4 rounded w-80 max-h-[80vh] flex flex-col">
                <div className="flex justify-between items-center mb-2">
                    <h3 className="text-lg font-bold">Support Chat</h3>
                    <button onClick={() => { trackEvent('support_modal_closed'); setVisible(false); }}>x</button>
                </div>
                {!started ? (
                    <>
                        <input
                            type="email"
                            className="w-full border p-2 mb-2"
                            placeholder="Your email"
                            value={email}
                            required
                            onFocus={() => trackEvent('support_email_input')} onChange={(e) => setEmail(e.target.value)}
                        />
                        {emailError && (
                            <p className="text-red-600 text-sm mb-2">{emailError}</p>
                        )}
                        <button
                            className="bg-blue-600 text-white p-2 disabled:opacity-50"
                            disabled={!isValidEmail(email)}
                            onClick={() => {
                                if (!isValidEmail(email)) {
                                    trackEvent('support_start_chat_invalid_email')
                                    setEmailError('Please enter a valid email');
                                    return;
                                }
                                trackEvent('support_start_chat_pressed')
                                setEmailError('');
                                setStarted(true);
                                loadMessages();
                            }}
                        >
                            Start Chat
                        </button>
                    </>
                ) : (
                    <>
                        <div className="flex flex-col overflow-y-auto mb-2 flex-1">
                            {messages.map((msg, idx) => (
                                <div key={idx} className={msg.from === 'user' ? 'text-right' : 'text-left'}>
                                    <div className="text-xs text-gray-500">
                                        {msg.time ? new Date(msg.time * 1000).toLocaleString() : ''}
                                    </div>
                                    <span className={msg.from === 'admin' ? 'inline-block bg-green-600 text-white px-2 py-1 rounded my-1' : 'inline-block bg-gray-200 px-2 py-1 rounded my-1'}>
                                        {msg.message}
                                    </span>
                                    {msg.image && (
                                        <img src={msg.image} alt="attachment" className="max-w-xs max-h-60 inline-block my-1" />
                                    )}
                                </div>
                            ))}
                        </div>
                        <input
                            className="w-full border p-2 mb-2"
                            value={input}
                            onFocus={() => trackEvent('support_message_input')} onChange={(e) => setInput(e.target.value)}
                            placeholder="Type a message..."
                        />
                        <input type="file" className="mb-2" onChange={e => { trackEvent('support_file_selected'); setFile(e.target.files[0]); }} />
                        <div className="flex justify-between">
                            <button className="bg-blue-600 text-white px-3 py-1" onClick={sendMessage}>
                                Send
                            </button>
                            <button className="bg-green-600 text-white px-3 py-1" onClick={resolve}>
                                Problem Resolved
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

